import { Close } from "@mui/icons-material";
import { Alert, Box, Button, Drawer, IconButton, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { DATE_INPUT_LANG } from "@/constants/dates";
import type { RecordPayoutPayload } from "@/interfaces/purchases";
import { formatAmount } from "@/utils/number-format";

const money = (v: string | null) => (v == null ? "—" : formatAmount(Number(v)));

/**
 * Records a Stripe payout that reached the bank when the webhook never fired. Shared by invoices and
 * credit purchases — the constraint and the entry are identical, only the subject differs.
 *
 * Only the fee is asked for: gross is what was actually charged, so net derives. The date is the day
 * the money landed and becomes the ACCOUNTING date, so a payout that arrived on the 14th books to
 * the 14th even when it is keyed on the 20th.
 */
export default function RecordPayoutDrawer({
  subject,
  grossUsd,
  currency = "USD",
  open,
  onClose,
  onSubmit,
  isPending,
}: {
  /** What is being settled, e.g. "Invoice INV-2026-08-000012" or "Purchase #14". */
  subject: string;
  grossUsd: number;
  currency?: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: RecordPayoutPayload) => Promise<unknown>;
  isPending: boolean;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [settlementDate, setSettlementDate] = useState(today);
  const [feeUsd, setFeeUsd] = useState("0");
  const [stripePayoutId, setStripePayoutId] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const gross = grossUsd;
  const fee = Number(feeUsd || 0);
  const net = gross - (Number.isFinite(fee) ? fee : 0);
  const feeInvalid = !Number.isFinite(fee) || fee < 0 || fee > gross;
  const canSubmit = settlementDate !== "" && reason.trim() !== "" && !feeInvalid;

  const submit = async () => {
    setError(null);
    try {
      await onSubmit({
        settlementDate,
        feeUsd: fee,
        stripePayoutId: stripePayoutId.trim() || undefined,
        reason: reason.trim(),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't record the payout.");
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: "100vw", sm: 440 }, p: 3, fontFamily: "var(--font-lato)" }}>
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Record payout</Typography>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mb: 3 }}>
          {subject} · {currency} {money(String(gross))}
        </Typography>

        <TextField
          label="Date the money landed"
          type="date"
          lang={DATE_INPUT_LANG}
          fullWidth
          size="small"
          value={settlementDate}
          onChange={(e) => setSettlementDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText="Becomes the accounting date — use the day it actually reached the bank."
          sx={{ mb: 2.5 }}
        />

        <TextField
          label="Stripe fee"
          type="number"
          fullWidth
          size="small"
          value={feeUsd}
          onChange={(e) => setFeeUsd(e.target.value)}
          error={feeInvalid}
          helperText={
            feeInvalid
              ? `Must be between 0 and ${money(String(gross))}`
              : "From the Stripe payout report. 0 is fine if unknown."
          }
          sx={{ mb: 2.5 }}
        />

        <TextField
          label="Stripe payout id (optional)"
          fullWidth
          size="small"
          value={stripePayoutId}
          onChange={(e) => setStripePayoutId(e.target.value)}
          placeholder="po_..."
          sx={{ mb: 2.5 }}
        />

        <TextField
          label="Reason"
          fullWidth
          size="small"
          multiline
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          helperText="Why this is being recorded by hand. Kept on the purchase's activity log."
          sx={{ mb: 3 }}
        />

        {/* What will actually be posted — no surprises after the fact. */}
        <Box
          sx={{
            border: "1px solid var(--color-card-border)",
            borderRadius: "var(--radius-card)",
            p: 2,
            mb: 3,
            fontSize: 13,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: "0.6px",
              textTransform: "uppercase",
              color: "var(--color-graphite)",
              mb: 1,
            }}
          >
            Will post
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto", rowGap: 0.5 }}>
            <span>DR Cash</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{money(String(net))}</span>
            <span>DR Stripe fees</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{money(String(fee || 0))}</span>
            <span>CR Stripe Clearing</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>{money(String(gross))}</span>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button variant="outlined" fullWidth onClick={onClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            fullWidth
            disabled={!canSubmit || isPending}
            onClick={submit}
            sx={{
              textTransform: "none",
              bgcolor: "var(--color-rodeo-black)",
              "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
            }}
          >
            {isPending ? "Recording…" : "Record paid"}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
